import { CheckIcon, CircleCrossIcon, CirclePauseIcon, DisabledIcon, DurationIcon, WaitlistIcon } from "@xola/icons";
import { Badge, Tooltip } from "@xola/ui-kit";
import clsx from "clsx";
import { PurchaseItemStatus } from "common";
import React from "react";
import { PurchaseItem } from "types";

/**
 * Show the right icon depending on the status of the purchase item.
 * Or the dispute badge in case if dispute was submitted for a purchase.
 */
interface StatusIconProps {
    item: PurchaseItem;
    displayText?: boolean;
    skipConfirmed?: boolean;
    displayTextForAllStatus?: boolean;
    className?: string;
}

export const StatusIcon = ({
    item,
    className,
    displayText = true,
    skipConfirmed = false,
    displayTextForAllStatus = false,
}: StatusIconProps) => {
    if (!statusMap[item.status]) {
        return null;
    }

    if (item.status === PurchaseItemStatus.CONFIRMED && skipConfirmed) {
        return null;
    }

    const status = statusMap[item.status];

    return (
        <Tooltip content={status.title}>
            <div
                className={clsx("inline-flex items-center tracking-tightest", className)}
                data-testid="purchase-status-icon"
            >
                {(displayTextForAllStatus || statusForTextDisplay.has(item.status)) && displayText ? (
                    <Badge color={status.color} icon={React.cloneElement(status.icon, { size: "small" })}>
                        {status.title}
                    </Badge>
                ) : (
                    status.icon
                )}
            </div>
        </Tooltip>
    );
};

const statusForTextDisplay = new Set([
    PurchaseItemStatus.CANCELED,
    PurchaseItemStatus.DECLINED,
    PurchaseItemStatus.PENDING_ACCEPT,
    PurchaseItemStatus.PENDING_MINIMUM,
    PurchaseItemStatus.WAITLIST_CONFIRMED,
]);

export interface StatusInfo {
    color: string;
    icon: JSX.Element;
    title: string;
}

export interface StatusMap {
    [key: string]: StatusInfo;
}

export const statusMap: StatusMap = {
    [PurchaseItemStatus.PENDING_ACCEPT]: {
        color: "warning",
        icon: <CirclePauseIcon size="medium" className="text-warning" />,
        title: "Pending Confirmation",
    },
    [PurchaseItemStatus.PENDING_MINIMUM]: {
        color: "warning",
        icon: <CirclePauseIcon size="medium" className="text-warning" />,
        title: "Pending Minimum",
    },
    [PurchaseItemStatus.CONFIRMED]: {
        color: "success",
        icon: <CheckIcon size="medium" className="text-success" />,
        title: "Confirmed",
    },
    [PurchaseItemStatus.DECLINED]: {
        color: "danger",
        icon: <DisabledIcon size="medium" className="text-danger" />,
        title: "Declined",
    },
    [PurchaseItemStatus.CANCELED]: {
        color: "danger",
        icon: <CircleCrossIcon size="medium" className="text-danger" />,
        title: "Canceled",
    },
    [PurchaseItemStatus.DRAFT]: {
        color: "secondary",
        icon: <DurationIcon size="medium" />,
        title: "Draft",
    },
    [PurchaseItemStatus.HOLD]: {
        color: "caution",
        icon: <DurationIcon size="medium" />,
        title: "On Hold",
    },
    [PurchaseItemStatus.WAITLIST_CREATED]: {
        color: "warning",
        icon: <WaitlistIcon size="medium" className="text-success" />,
        title: "Waitlist",
    },
};
